import { useState } from "preact/hooks";
import { TextField } from "../components/TextField";
import { PRESET_STYLES } from "../config/presets";
import { useConfirmBooking } from "../mutations/useConfirmBooking";
import { type Slot } from "../queries/useCampaignAvailability";
import {
	formatMonthDay,
	formatTime,
	formatWeekday,
	tzAbbrev,
	ymd,
} from "./format";

const MAIA_STYLES = PRESET_STYLES.maia;

type ConfirmStepProps = {
	slot: Slot;
	timezone: string;
	advisorName: string;
	advisorId: number;
	campaignId: string;
	attribution: Record<string, string>;
	onBack: () => void;
	onSuccess: (leadName: string, leadEmail: string) => void;
};

export function ConfirmStep({
	slot,
	timezone,
	advisorName,
	advisorId,
	campaignId,
	attribution,
	onBack,
	onSuccess,
}: ConfirmStepProps) {
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [error, setError] = useState<string | null>(null);

	const trimmedName = name.trim();
	const trimmedEmail = email.trim();

	const mutation = useConfirmBooking(
		(_res) => onSuccess(trimmedName, trimmedEmail),
		() => setError("Something went wrong booking that time. Please try again."),
	);

	const valid =
		trimmedName.length >= 2 &&
		/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmedEmail);

	function submit(e: Event) {
		e.preventDefault();
		if (!valid || mutation.isPending) return;
		setError(null);
		const [firstname, ...rest] = trimmedName.split(" ");
		mutation.mutate({
			campaignId,
			advisorId,
			prospectFirstname: firstname,
			prospectLastname: rest.join(" "),
			prospectEmail: trimmedEmail,
			slotStart: slot.start,
			slotEnd: slot.end,
			timezone,
			attribution,
		});
	}

	const slotDateKey = ymd(slot.start, timezone);

	return (
		<form onSubmit={submit}>
			<p className="font-semibold tracking-[0.16em] uppercase text-xs text-[#9c8230]">
				Step 02 — Confirm
			</p>
			<h1 className='font-black tracking-tight leading-[0.95] font-["Gotham_Narrow","Helvetica_Neue",Arial,sans-serif] mt-3 text-5xl sm:text-6xl'>
				YOUR DETAILS.
			</h1>
			<p className="mt-4 max-w-md text-base text-[#2c4239]">
				You're booking{" "}
				<span className="font-semibold">
					{formatWeekday(slotDateKey, timezone)},{" "}
					{formatMonthDay(slotDateKey, timezone)} ·{" "}
					{formatTime(slot.start, timezone)}
				</span>{" "}
				<span className="font-semibold">{tzAbbrev(timezone)}</span> with{" "}
				{advisorName}.
			</p>

			<div className="mt-10 grid max-w-lg gap-5 border-t border-[#1a2e26]/15 pt-8">
				<TextField
					id="name"
					label="Your name"
					value={name}
					onInput={setName}
					placeholder="Alex Chen"
					labelClassName={MAIA_STYLES.labelClassName}
					inputClassName={MAIA_STYLES.inputClassName}
				/>
				<TextField
					id="email"
					label="Email"
					value={email}
					onInput={setEmail}
					placeholder="alex@example.com"
					type="email"
					labelClassName={MAIA_STYLES.labelClassName}
					inputClassName={MAIA_STYLES.inputClassName}
				/>
			</div>

			{error && <p className="mt-4 text-sm text-[#9c2f1f]">{error}</p>}

			<div className="mt-8 flex max-w-lg flex-col-reverse items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
				<button
					type="button"
					onClick={onBack}
					disabled={mutation.isPending}
					className="font-semibold tracking-[0.16em] uppercase text-xs underline-offset-4 hover:underline disabled:opacity-50"
				>
					← Back to times
				</button>
				<button
					type="submit"
					disabled={!valid || mutation.isPending}
					className='inline-flex items-center justify-center gap-2 rounded-full bg-[#17372e] px-7 py-3 text-sm font-semibold tracking-wide text-[#e0e1b7] uppercase shadow-sm transition-colors hover:bg-[#2c4239] disabled:cursor-not-allowed disabled:opacity-50 font-["Gotham_Narrow","Helvetica_Neue",Arial,sans-serif]'
				>
					{mutation.isPending && <Spinner />}
					{mutation.isPending ? "Booking…" : "Confirm booking"}
				</button>
			</div>
		</form>
	);
}

function Spinner() {
	return (
		<svg
			className="h-4 w-4 animate-spin"
			viewBox="0 0 24 24"
			fill="none"
			aria-hidden
		>
			<circle
				className="opacity-25"
				cx="12"
				cy="12"
				r="10"
				stroke="currentColor"
				strokeWidth="3"
			/>
			<path
				className="opacity-75"
				fill="currentColor"
				d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"
			/>
		</svg>
	);
}
